import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { updateAccount as updateAccountApi } from '../services/api';
import { useDialogClickOutside, useModalScrollLock } from '../hooks';
import { Input, CustomSelect } from '@redpanda/ui';
import { FormFieldWithError, getAccountTypeOptions } from './forms';
import { toast } from 'react-hot-toast';
import { useAccountStore } from '../stores/accountStore';

interface Account {
  id: string;
  code: string;
  name: string;
  type: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
  description?: string;
}

interface AccountEditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  account: Account | null;
  onAccountUpdated?: (updatedAccount?: Account) => void;
}

interface FormData {
  code: string;
  name: string;
  type: 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE';
  description?: string;
}

const AccountEditDialog: React.FC<AccountEditDialogProps> = ({
  isOpen,
  onClose,
  account,
  onAccountUpdated
}) => {
  const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm<FormData>();

  const { t } = useTranslation();
  const { updateAccount } = useAccountStore();
  
  // 鎖定背景滾動
  useModalScrollLock(isOpen);

  // 設置表單初始值
  useEffect(() => {
    if (isOpen && account) {
      setValue('code', account.code);
      setValue('name', account.name);
      setValue('type', account.type);
      setValue('description', account.description || '');
    }
  }, [isOpen, account, setValue]);

  const handleClose = () => {
    reset();
    onClose();
  };

  // 點擊外部關閉 dialog
  const handleBackgroundClick = useDialogClickOutside(handleClose);

  const onSubmit = async (data: FormData) => {
    if (!account) return;

    // UPDATE 樂觀更新
    const originalAccount = { ...account };
    const optimisticData = { ...account, ...data };

    updateAccount(account.id, optimisticData);
    handleClose();
    if (onAccountUpdated) onAccountUpdated(optimisticData);

    try {
      const response = await updateAccountApi(account.id, data);
      const realAccount = response?.data || optimisticData;
      updateAccount(account.id, realAccount);
      toast.success(t('common.messages.updateSuccess'));
    } catch (error: any) {
      updateAccount(account.id, originalAccount);
      const msg = error.response?.data?.error || error.response?.data?.message || error.message || '更新會計科目失敗';
      toast.error(msg);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
      onMouseDown={handleBackgroundClick}
    >
      <div 
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto overflow-x-visible"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 對話框標題 */}
        <div className="flex justify-between items-center px-6 py-3 border-b border-gray-200 dark:border-slate-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            編輯會計科目
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* 對話框內容 */}
        <div className="p-6">

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 overflow-visible">

                <FormFieldWithError
                  label={t('accounts.code')}
                  required={true}
                  error={errors.code?.message}
                  htmlFor="code"
                >
                  <Input
                    id="code"
                    type="text"
                    {...register('code', { 
                      required: '必填',
                      pattern: {
                        value: /^[0-9]{4,6}$/,
                        message: '科目代號必須為4-6位數字'
                      }
                    })}
                    variant={errors.code ? "error" : "default"}
                  />
                </FormFieldWithError>

                <FormFieldWithError
                  label={t('accounts.name')}
                  required={true}
                  error={errors.name?.message}
                  htmlFor="name"
                >
                  <Input
                    id="name"
                    type="text"
                    {...register('name', { required: '必填' })}
                    variant={errors.name ? "error" : "default"}
                  />
                </FormFieldWithError>

                <FormFieldWithError
                  label={t('accounts.type')}
                  required={true}
                  error={errors.type?.message}
                  htmlFor="type"
                >
                  <CustomSelect
                    id="type"
                    {...register('type', { required: '必填' })}
                    value={watch('type') || ''}
                    onChange={(value) => setValue('type', value as 'ASSET' | 'LIABILITY' | 'EQUITY' | 'INCOME' | 'EXPENSE')}
                    variant={errors.type ? "error" : "default"}
                    options={[
                      { value: '', label: t('accounts.selectAccountType') },
                      ...getAccountTypeOptions(t)
                    ]}
                  />
                </FormFieldWithError>

                <FormFieldWithError
                  label={t('accounts.description')}
                  required={false}
                  error={errors.description?.message}
                  htmlFor="description"
                >
                  <Input
                    id="description"
                    type="text"
                    {...register('description')}
                    variant={errors.description ? "error" : "default"}
                  />
                </FormFieldWithError>

                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse pt-6 border-t border-gray-200 dark:border-slate-700">
                  <button
                    type="submit"
                    className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto"
                  >
                    {t('common.actions.update')}
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded-md"
                    onClick={handleClose}
                  >
                    {t('common.actions.cancel')}
                  </button>
                </div>
              </form>
        </div>
      </div>
    </div>
  );
};

export default AccountEditDialog;